import useResizeObserver from '@react-hook/resize-observer'
import { useState } from 'react'
import { useRef } from 'react'
import styled from 'styled-components'
import { breakPoints, measurments } from '../utils/config'
import ProfileMenuIcon from './ProfileMenuIcon'
import {
  calculateForeground,
  lightenDarkenColor,
  LucideIconName,
  WebsiteConfigBM,
  WebsiteConfigPage,
} from '@bm-js/atlascloud-shared'
import { Icon, ResponsiveImage } from '@bm-js/atlascloud-ui'

// TODO: align with WebsiteColors
type NavWebsiteColors = {
  dark: string
  base: string
  inverse: string
  darkInverse: string
  light: string
  lightInverse: string
}

const StyledNav2 = styled.div<{ $colors: NavWebsiteColors }>`
  position: absolute;
  top: 0;
  width: 100%;
  background: ${({ $colors }) => $colors.base};
  z-index: 11;
  a {
    white-space: nowrap;
  }
  .nav-inner {
    color: ${({ $colors }) => $colors.inverse};
    height: ${measurments.navHeight}px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .nav-left {
      display: flex;
      .nav-logo {
        margin-right: 1.5rem;
        padding-top: 0.75rem;
        padding-bottom: 0.75rem;
        .nav-logo-mobile {
          display: none;
        }
        img {
          height: calc(${measurments.navHeight}px - 1.5rem);
          max-width: 100%;
          width: revert;
          max-height: 100%;
          object-fit: contain;
          object-position: left;
        }
      }
      .nav-links {
        display: flex;
      }
    }
  }
  .nav-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    .hamburger {
      display: none;
    }
  }
  &.tablet {
    .nav-inner {
      .nav-left {
        .nav-logo {
          .nav-logo-mobile {
            display: block;
          }
          .nav-logo-desktop {
            display: none;
          }
        }
        .nav-links {
          position: absolute;
          top: ${measurments.navHeight}px;
          display: block;
          left: 0;
          right: 0;
          width: 100%;
          background: ${({ $colors }) => $colors.dark};
          opacity: 0;
          pointer-events: none;
          transition: 100ms ease-in-out;
        }
      }
    }
    .nav-right {
      .hamburger {
        display: block;
      }
    }
    &.expanded {
      .nav-inner {
        .nav-left {
          .nav-links {
            opacity: 1;
            pointer-events: initial;
          }
        }
      }
    }
  }
`

type Props = {
  websiteConfig: WebsiteConfigBM
  getPagePath: (b: boolean) => WebsiteConfigPage[]
  setActivePageFromNav: (p: WebsiteConfigPage) => void
  features: string[]
}

const Nav2 = ({
  websiteConfig,
  getPagePath,
  setActivePageFromNav: sepfn,
  features,
}: Props) => {
  const setActivePageFromNav = (p: WebsiteConfigPage) => {
    setExpanded(false)
    sepfn(p)
  }

  const $colors: NavWebsiteColors = {
    base: websiteConfig.colors.base,
    light: lightenDarkenColor(websiteConfig.colors.base, 30),
    lightInverse: calculateForeground(
      lightenDarkenColor(websiteConfig.colors.base, 30)
    ),

    inverse: calculateForeground(websiteConfig.colors.base),
    dark: lightenDarkenColor(websiteConfig.colors.base, -20),
    darkInverse: calculateForeground(
      lightenDarkenColor(websiteConfig.colors.base, -20)
    ),
  }

  const target = useRef(null)
  const [width, setWidth] = useState(0)
  const [expanded, setExpanded] = useState(false)
  useResizeObserver(target, (entry) => {
    setWidth(entry.contentRect.width)
  })

  const className = `${width < breakPoints.tablet ? 'tablet' : ''} ${
    width < breakPoints.mobile ? 'mobile' : ''
  }`

  return (
    <StyledNav2
      $colors={$colors}
      ref={target}
      className={`${className} ${expanded ? 'expanded' : ''}`}
    >
      <div className='nav-inner content'>
        <div className='nav-left'>
          {(websiteConfig.logoWide || websiteConfig.logoSquare) && (
            <div
              className='nav-logo pointer'
              onClick={() => setActivePageFromNav(websiteConfig.pages[0])}
            >
              <div className='nav-logo-desktop'>
                <ResponsiveImage file={websiteConfig.logoWide} />
              </div>
              <div className='nav-logo-mobile'>
                <ResponsiveImage file={websiteConfig.logoSquare} />
              </div>
            </div>
          )}
          <div className='nav-links'>
            {websiteConfig.pages.map((parentPage) => (
              <Nav2Parent
                className={className}
                $colors={$colors}
                getPagePath={getPagePath}
                key={parentPage.uuid}
                parentPage={parentPage}
                setActivePageFromNav={setActivePageFromNav}
              />
            ))}
          </div>
        </div>
        <div className='nav-right'>
          <div
            className='pointer hamburger text-customerInverse'
            onClick={() => setExpanded(!expanded)}
          >
            <Icon
              name={expanded ? LucideIconName.X : LucideIconName.Menu}
              className='size-5'
            />
          </div>
          <ProfileMenuIcon features={features} />
        </div>
      </div>
    </StyledNav2>
  )
}

export default Nav2

const StyledNav2Parent = styled.div<{ $colors: NavWebsiteColors }>`
  .parent-outer {
    .parent-link {
      height: ${measurments.navHeight}px;
      display: flex;
      align-items: center;
    }
    transition: 100ms linear;
    padding-left: 0.7rem;
    padding-right: 0.7rem;
    display: flex;
    align-items: center;
    svg {
      width: 16px;
      height: 16px;
      margin-left: 0.5rem;
    }
  }
  .children-container {
    position: absolute;
    top: ${measurments.navHeight}px;
    left: 0;
    height: ${measurments.navHeight}px;
    background: ${({ $colors }) => $colors.light};
    color: ${({ $colors }) => $colors.lightInverse};
    width: 100%;
    opacity: 0;
    pointer-events: none;
    .children-container-inner {
      display: flex;
      align-items: center;
      height: 100%;
    }
  }
  &:not(.tablet) {
    &.is-active {
      .parent-outer {
        background: ${({ $colors }) => $colors.light};
      }
      .children-container {
        opacity: 1;
        pointer-events: initial;
      }
    }
    &:hover {
      .parent-outer {
        background: ${({ $colors }) => $colors.light};
      }
      .children-container {
        opacity: 1;
        pointer-events: initial;
        z-index: 10;
      }
    }
  }
  &.tablet {
    .parent-outer {
      justify-content: space-between;
      padding-left: 1rem;
      padding-right: 1rem;
    }
    .children-container {
      position: revert;
      top: revert;
      left: revert;
      height: revert;
      opacity: 1;
      max-height: 0;
      transition: 200ms ease-in-out;
      overflow: hidden;
      .children-container-inner {
        display: block;
      }
    }
    &.expanded {
      .children-container {
        max-height: 100vh;
        padding-left: 0.5rem;
        pointer-events: initial;
      }
    }
  }
`

type ParentProps = {
  parentPage: WebsiteConfigPage
  setActivePageFromNav: (p: WebsiteConfigPage) => void
  getPagePath: (b: boolean) => WebsiteConfigPage[]
  $colors: NavWebsiteColors
  className: string
}

const Nav2Parent = ({
  parentPage,
  setActivePageFromNav,
  getPagePath,
  $colors,
  className,
}: ParentProps) => {
  const [expanded, setExpanded] = useState(false)

  const activeParentUuid = getPagePath(true)?.[0]?.uuid
  const shouldExpand = activeParentUuid === parentPage.uuid

  return (
    <StyledNav2Parent
      className={`${shouldExpand ? 'is-active' : ''} ${className} ${
        expanded ? 'expanded' : ''
      }`}
      key={parentPage.uuid}
      $colors={$colors}
    >
      <div className='parent-outer'>
        <a
          href='#'
          className='clean parent-link'
          onClick={() => setActivePageFromNav(parentPage)}
        >
          {parentPage.viewName}
          {!!parentPage.authOnly && (
            <span className='text-customerInverse'>
              <Icon name={LucideIconName.LockKeyhole} className='size-5' />
            </span>
          )}
        </a>
        {!!parentPage.children?.length && (
          <div
            onClick={() => setExpanded(!expanded)}
            className='pointer text-customerInverse'
          >
            <Icon
              name={
                expanded ? LucideIconName.ChevronUp : LucideIconName.ChevronDown
              }
              className='size-5'
            />
          </div>
        )}
      </div>
      {!!parentPage.children?.length && (
        <div className={`children-container`}>
          <div className='children-container-inner content'>
            {parentPage.children.map((childPage) => (
              <Nav2Child
                className={className}
                key={childPage.uuid}
                childPage={childPage}
                setActivePageFromNav={setActivePageFromNav}
                parentPage={parentPage}
                $colors={$colors}
              />
            ))}
          </div>
        </div>
      )}
    </StyledNav2Parent>
  )
}

const StyledNav2Child = styled.div<{ $colors: NavWebsiteColors }>`
  position: relative;
  .child-outer {
    .child-link {
      display: flex;
      align-items: center;
      height: ${measurments.navHeight}px;
    }
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    display: flex;
    align-items: center;
    svg {
      margin-left: 0.5rem;
      width: 16px;
      height: 16px;
    }
  }
  .grandchildren-container {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    background: ${({ $colors }) => $colors.light};
    position: absolute;
    top: ${measurments.navHeight}px;
    left: 0;
    opacity: 0;
    pointer-events: none;
  }
  &:not(.tablet) {
    &:hover {
      .grandchildren-container {
        opacity: 1;
        pointer-events: initial;
      }
    }
  }
  &.tablet {
    .grandchildren-container {
      position: revert;
      top: revert;
      left: revert;
      opacity: 1;
      max-height: 0;
      overflow: hidden;
      padding-top: 0;
      padding-bottom: 0;
      transition: 200ms ease-in-out;
    }
    .child-outer {
      justify-content: space-between;
      padding-right: 0;
    }
    &.expanded {
      .grandchildren-container {
        max-height: 100vh;
        pointer-events: initial;
        padding-left: 0.5rem;
      }
    }
  }
`

type ChildProps = {
  childPage: WebsiteConfigPage
  setActivePageFromNav: (p: WebsiteConfigPage) => void
  parentPage: WebsiteConfigPage
  $colors: NavWebsiteColors
  className: string
}

const Nav2Child = ({
  childPage,
  setActivePageFromNav,
  parentPage,
  $colors,
  className,
}: ChildProps) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <StyledNav2Child
      $colors={$colors}
      className={`${className} ${expanded ? 'expanded' : ''}`}
    >
      <div className='child-outer'>
        <a
          className='child-link'
          href='#'
          onClick={() => setActivePageFromNav(childPage)}
        >
          {childPage.viewName}
          {!!childPage.authOnly && (
            <span className='text-customerInverse'>
              <Icon name={LucideIconName.LockKeyhole} className='size-5' />
            </span>
          )}
        </a>
        {!!childPage.children?.length && (
          <div
            className='pointer text-customerInverse'
            onClick={() => setExpanded(!expanded)}
          >
            <Icon
              name={
                expanded ? LucideIconName.ChevronUp : LucideIconName.ChevronDown
              }
            />
          </div>
        )}
      </div>
      {!!(childPage.children || []).length && (
        <div className='grandchildren-container'>
          {(childPage.children || []).map((grandChildPage) => (
            <Nav2GrandChild
              key={grandChildPage.uuid}
              grandChildPage={grandChildPage}
              $colors={$colors}
              setActivePageFromNav={setActivePageFromNav}
              childPage={childPage}
              parentPage={parentPage}
              className={className}
            />
          ))}
        </div>
      )}
    </StyledNav2Child>
  )
}

const StyledNav2GrandChild = styled.div<{ $colors: NavWebsiteColors }>`
  position: relative;
  .grand-child-outer {
    .grand-child-link {
      display: flex;
      align-items: center;
      margin: 0;
      white-space: nowrap;
    }
    padding-top: 0.3rem;
    justify-content: space-between;
    padding-bottom: 0.3rem;
    padding-left: 1em;
    padding-right: 1rem;
    display: flex;
    align-items: center;
    svg {
      width: 16px;
      height: 16px;
      margin-left: 0.5rem;
    }
  }
  .grand-grand-children-container {
    position: absolute;
    top: 0;
    left: 100%;
    background: ${({ $colors }) => $colors.light};
    opacity: 0;
    pointer-events: none;
    padding: 0.5rem;
    .grand-grand-child-link {
      display: flex;
      align-items: center;
      padding: 0.5rem;
      svg {
        width: 16px;
        height: 16px;
        margin-left: 0.5rem;
      }
    }
  }
  &:not(.tablet) {
    &:hover {
      .grand-grand-children-container {
        opacity: 1;
        pointer-events: initial;
      }
    }
  }
  &.tablet {
    .grand-child-outer {
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      padding-right: 0;
      svg {
        transform: rotateZ(90deg);
      }
    }
    .grand-grand-children-container {
      position: revert;
      top: revert;
      left: revert;
      opacity: 1;
      overflow: hidden;
      max-height: 0;
      transition: 200ms ease-in-out;
      padding: 0;
      padding-left: 0.5rem;
    }
    &.expanded {
      .grand-grand-children-container {
        max-height: 100vh;
        padding-left: 1.5rem;
      }
    }
  }
`

type GrandChildProps = {
  grandChildPage: WebsiteConfigPage
  setActivePageFromNav: (p: WebsiteConfigPage) => void
  childPage: WebsiteConfigPage
  parentPage: WebsiteConfigPage
  $colors: NavWebsiteColors
  className: string
}

const Nav2GrandChild = ({
  setActivePageFromNav,
  grandChildPage,
  className,
  $colors,
}: GrandChildProps) => {
  const [expanded, setExpanded] = useState(false)
  return (
    <StyledNav2GrandChild
      $colors={$colors}
      className={`${className} ${expanded ? 'expanded' : ''}`}
    >
      <div className='grand-child-outer'>
        <a
          href='#'
          className='grand-child-link'
          onClick={() => setActivePageFromNav(grandChildPage)}
        >
          {grandChildPage.viewName}
          {!!grandChildPage.authOnly && (
            <span className='text-customerInverse'>
              <Icon name={LucideIconName.LockKeyhole} className='size-5' />
            </span>
          )}
        </a>
        {!!grandChildPage.children?.length && (
          <div className='pointer' onClick={() => setExpanded(!expanded)}>
            <Icon name={LucideIconName.ChevronRight} className='size-5' />
          </div>
        )}
      </div>
      {!!grandChildPage.children?.length && (
        <div className='grand-grand-children-container'>
          {grandChildPage.children.map((grandGrandChildPage) => (
            <div
              className='grand-grand-child-page'
              key={grandGrandChildPage.uuid}
            >
              <a
                href='#'
                className='grand-grand-child-link'
                onClick={() => setActivePageFromNav(grandGrandChildPage)}
              >
                {grandGrandChildPage.viewName}
                {!!grandGrandChildPage.authOnly && (
                  <span className='text-customerInverse'>
                    <Icon
                      name={LucideIconName.LockKeyhole}
                      className='size-5'
                    />
                  </span>
                )}
              </a>
            </div>
          ))}
        </div>
      )}
    </StyledNav2GrandChild>
  )
}
